import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";
import SortableTh, { sortRows } from "@/components/SortableTh";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function Groups() {
    const { user } = useAuth();
    const isAdmin = ["admin", "superadmin"].includes(user?.role);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState("");
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({ name: "", description: "" });
    const [saving, setSaving] = useState(false);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState({ field: "name", dir: "asc" });

    const filtered = useMemo(() => {
        const base = rows.filter((r) =>
            !q || r.name.toLowerCase().includes(q.toLowerCase()) || (r.description || "").toLowerCase().includes(q.toLowerCase())
        );
        return sortRows(base, sort);
    }, [rows, q, sort]);

    const load = async () => {
        try { const { data } = await api.get("/groups"); setRows(data); }
        catch (e) { setError(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => { setForm({ name: "", description: "" }); setModal({ mode: "create" }); };
    const openEdit = (g) => { setForm({ name: g.name, description: g.description || "" }); setModal({ mode: "edit", group: g }); };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true); setError("");
        try {
            if (modal.mode === "create") await api.post("/groups", form);
            else await api.patch(`/groups/${modal.group.group_id}`, form);
            setModal(null); await load();
        } catch (err) { setError(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const remove = async (g) => {
        if (!window.confirm(`¿Eliminar grupo "${g.name}"?`)) return;
        try { await api.delete(`/groups/${g.group_id}`); await load(); }
        catch (err) { setError(formatApiError(err)); }
    };

    return (
        <div data-testid="groups-page">
            <div className="page-head">
                <div>
                    <h1>Grupos / Colas</h1>
                    <p>Colas donde los agentes atienden tickets — por ejemplo Soporte, Ventas, Facturación.</p>
                </div>
                {isAdmin && (
                    <button className="btn btn-primary" onClick={openCreate} data-testid="group-create-btn">
                        <Plus size={16} /> Nuevo grupo
                    </button>
                )}
            </div>

            {error && <div className="alert alert-error" data-testid="groups-error">{error}</div>}

            <div className="card">
                <div className="card-header">
                    <h3>Listado</h3>
                    <div className="tbar"><input className="input" style={{ width: 280 }} placeholder="Buscar por nombre o descripción…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="groups-search" /></div>
                </div>
                <div className="table-wrap">
                    <table className="t">
                        <thead><tr><SortableTh field="name" sort={sort} onChange={setSort}>Nombre</SortableTh><SortableTh field="description" sort={sort} onChange={setSort}>Descripción</SortableTh><SortableTh field="group_id" sort={sort} onChange={setSort}>ID</SortableTh>{isAdmin && <th style={{ textAlign: "right" }}>Acciones</th>}</tr></thead>
                        <tbody>
                            {filtered.length === 0 && (<tr><td colSpan={isAdmin ? 4 : 3} className="text-muted" style={{ textAlign: "center", padding: 30 }}>Sin grupos.</td></tr>)}
                            {filtered.map((g) => (
                                <tr key={g.group_id} data-testid={`group-row-${g.group_id}`}>
                                    <td><b>{g.name}</b></td>
                                    <td className="text-muted">{g.description || "—"}</td>
                                    <td className="id-mono">{g.group_id}</td>
                                    {isAdmin && (
                                        <td>
                                            <div className="actions">
                                                <button className="btn btn-ghost btn-sm" onClick={() => openEdit(g)} data-testid={`group-edit-${g.group_id}`}><Pencil size={13} /> Editar</button>
                                                <button className="btn btn-danger btn-sm" onClick={() => remove(g)} data-testid={`group-delete-${g.group_id}`}><Trash2 size={13} /></button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal
                open={!!modal}
                onClose={() => setModal(null)}
                title={modal?.mode === "create" ? "Nuevo grupo" : "Editar grupo"}
                testId="group-modal"
                footer={
                    <>
                        <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
                        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="group-modal-save">
                            {saving ? <span className="loader" /> : "Guardar"}
                        </button>
                    </>
                }
            >
                <form onSubmit={save} className="row" style={{ gap: 14 }}>
                    <div className="field">
                        <label>Nombre *</label>
                        <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="group-modal-name" />
                    </div>
                    <div className="field">
                        <label>Descripción</label>
                        <textarea className="textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="group-modal-desc" />
                    </div>
                </form>
            </Modal>
        </div>
    );
}
